import { QueryClient, useMutation, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { QueryError } from 'errors'
import { Filterable } from 'types/index'
import { EditVariant, Place, ProductWithVariants, Variant, VariantBase } from 'types/products'
import { NewShortage } from 'types/shortage'
import { useIsProductModifiedStore } from '../routes/_auth/product.$productId.{-$section}'

export function useEditVariant() {
  const setIsModified = useIsProductModifiedStore((state) => state.setIsModified)
  const queryClient = useQueryClient()

  return useMutation<Variant, QueryError, EditVariant>({
    mutationKey: ['editVariant'],
    mutationFn: async (variant) => {
      return (await axios.put<Variant>(`/variants/${variant.id}`, variant)).data
    },
    meta: {
      closeModal: true,
      successMessage: 'Zaktualizowano produkt',
    },
    onSuccess: (updatedVariant) => {
      setIsModified(true)
      updateVariantData(updatedVariant, queryClient)
      queryClient.invalidateQueries({ queryKey: ['productsOnPlace'] })
      queryClient.invalidateQueries({ queryKey: ['variants'] })
    },
  })
}

export function useDeleteVariant() {
  const setIsModified = useIsProductModifiedStore((state) => state.setIsModified)
  const queryClient = useQueryClient()

  return useMutation<void, QueryError, Variant>({
    mutationKey: ['deleteVariant'],
    mutationFn: async (variant) => {
      await axios.delete(`/variants/${variant.id}`)
    },
    meta: {
      closeModal: true,
      successMessage: 'Usunięto produkt',
    },
    onSuccess: (_, deletedVariant) => {
      setIsModified(true)
      queryClient.setQueryData<ProductWithVariants>(['productWithVariants', deletedVariant.productId], (oldData) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          variants: {
            ...oldData.variants,
            [deletedVariant.category]: oldData.variants[deletedVariant.category].filter(
              (variant) => variant.id !== deletedVariant.id
            ),
          },
        }
      })
      queryClient.setQueryData(['productsOnPlace', deletedVariant.place.id], (prev: Array<ProductWithVariants>) => {
        if (!prev) return prev
        return prev.map((product) => {
          if (product.id !== deletedVariant.productId) {
            return product
          }
          return {
            ...product,
            variants: {
              ...product.variants,
              [deletedVariant.category]: product.variants[deletedVariant.category].filter(
                (variant) => variant.id !== deletedVariant.id
              ),
            },
          }
        })
      })
      queryClient.setQueriesData({ queryKey: ['variants'] }, (oldData: Filterable<Variant> | undefined) => {
        if (!oldData) return oldData
        let newData = [...oldData.data]
        const oldTotal = oldData.total
        newData = newData.filter((variant) => variant.id !== deletedVariant.id)
        return {
          total: oldData.data.length !== newData.length ? oldTotal - 1 : oldTotal,
          data: newData,
        }
      })
    },
  })
}

export function useTakeVariant() {
  const setIsModified = useIsProductModifiedStore((state) => state.setIsModified)
  const queryClient = useQueryClient()

  return useMutation<Variant | null, QueryError, { amount: number; variant: VariantBase }>({
    mutationKey: ['takeVariant'],
    mutationFn: async ({ variant, amount }) => {
      return (await axios.patch<Variant>(`/variants/${variant.id}/take`, { amount })).data
    },
    meta: {
      closeModal: true,
      successMessage: 'Pobrałeś produkt z magazynu',
    },
    onSuccess: (updatedVariant, { variant }) => {
      setIsModified(true)
      if (updatedVariant) {
        updateVariantData(updatedVariant, queryClient)
        queryClient.setQueryData<Array<ProductWithVariants>>(
          ['productsOnPlace', updatedVariant.place.id],
          (oldData) => {
            if (!oldData) return oldData
            return oldData.map((product) => {
              if (product.id !== updatedVariant.productId) {
                return product
              }
              return {
                ...product,
                variants: {
                  ...product.variants,
                  [updatedVariant.category]: product.variants[updatedVariant.category].map((variant) => {
                    if (variant.id === updatedVariant.id) {
                      return updatedVariant
                    }
                    return variant
                  }),
                },
              }
            })
          }
        )
      } else {
        queryClient.setQueryData<ProductWithVariants>(['productWithVariants', variant.productId], (oldData) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            variants: {
              ...oldData.variants,
              [variant.category]: oldData.variants[variant.category].filter((v) => {
                if (v.id !== variant.id) {
                  return true
                }
                return false
              }),
            },
          }
        })
      }
    },
  })
}

export function useMoveVariant() {
  const setIsModified = useIsProductModifiedStore((state) => state.setIsModified)
  const queryClient = useQueryClient()

  return useMutation<Variant, QueryError, { variantId: string; place: Place }>({
    mutationKey: ['moveVariant'],
    mutationFn: async ({ variantId, place }) => {
      return (await axios.patch<Variant>(`/variants/${variantId}/move`, place)).data
    },
    meta: {
      closeModal: true,
      successMessage: 'Przeniosłeś produkt na inną półkę',
    },
    onSuccess: (updatedVariant) => {
      setIsModified(true)
      updateVariantData(updatedVariant, queryClient)
      queryClient.invalidateQueries({ queryKey: ['productsOnPlace'] })
    },
  })
}

function updateVariantData(updatedVariant: Variant, queryClient: QueryClient) {
  queryClient.setQueryData<ProductWithVariants>(['productWithVariants', updatedVariant.productId], (oldData) => {
    if (!oldData) return oldData
    return {
      ...oldData,
      variants: {
        ...oldData.variants,
        [updatedVariant.category]: oldData.variants[updatedVariant.category].map((variant) => {
          if (variant.id === updatedVariant.id) {
            return updatedVariant
          }
          return variant
        }),
      },
    }
  })
}

export function useIsVariantAvailable() {
  return useMutation<boolean, QueryError, NewShortage>({
    mutationKey: ['variants', 'isAvailable'],
    mutationFn: async (params) => {
      const response = await axios.get<{ available: boolean }>('/variants/isAvailable', { params })
      return response.data.available
    },
  })
}
